<template>
  <div>
    <b-container>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Cuestionario médico de asegurabilidad</h6>
            </template>

            <p class="text-muted small mb-3">Responda Sí o No para cada persona a asegurar, según corresponda.</p>

            <div v-for="(persona, indexPersona) in personasConDatos" :key="'pers-' + indexPersona" class="mb-3">
              <b-card no-body class="cuestionario-persona-card">
                <b-card-header
                  role="button"
                  class="d-flex align-items-center justify-content-between py-2 cuestionario-persona-header"
                  @click="togglePersona(indexPersona)"
                >
                  <span class="font-weight-bold mb-0 cuestionario-persona-nombre">
                    {{
                      [persona.name, persona.lastName, persona.secondLastName]
                        .map((s) => (s || "").trim())
                        .filter(Boolean)
                        .join(" ") || "Persona " + (indexPersona + 1)
                    }}
                  </span>
                  <span class="d-flex align-items-center">
                    <b-button
                      variant="link"
                      size="sm"
                      class="p-0 mr-2 text-secondary d-inline-flex align-items-center"
                      :title="'Marcar todo No'"
                      @click.stop="marcarTodoNo(persona)"
                    >
                      <b-icon icon="dash-circle" font-scale="1.2" />
                    </b-button>
                    <b-button
                      variant="link"
                      size="sm"
                      class="p-0 mr-2 text-secondary d-inline-flex align-items-center"
                      :title="'Limpiar secciones'"
                      @click.stop="limpiarSeccionesPersona(persona)"
                    >
                      <b-icon icon="eraser" font-scale="1.2" />
                    </b-button>
                    <span class="cuestionario-contadas mr-3">
                      {{ contestadasCount(persona) }} de {{ preguntasCount(persona) }}
                    </span>
                    <b-icon
                      :icon="personasAbiertas[indexPersona] ? 'chevron-up' : 'chevron-down'"
                      font-scale="1.1"
                      class="cuestionario-persona-chevron"
                    />
                  </span>
                </b-card-header>
                <b-collapse :visible="personasAbiertas[indexPersona]" :id="'collapse-persona-' + indexPersona">
                  <b-card-body class="pt-2 pb-3">
                    <b-form-group
                      v-for="(pregunta, indexPregunta) in PREGUNTAS_CUESTIONARIO_MEDICO"
                      v-show="pregunta.code < 22 || persona.sex === 'F'"
                      :key="'p-' + indexPersona + '-' + indexPregunta"
                      class="cuestionario-pregunta"
                    >
                      <b-row align-v="center" no-gutters class="cuestionario-row">
                        <b-col class="pr-2">
                          <label class="col-form-label font-weight-normal mb-0 text-break">
                            {{ pregunta.code + ". " + pregunta.label }}
                          </label>
                        </b-col>

                        <b-col cols="auto" class="cuestionario-sino-wrap">
                          <!-- P22 mujeres: fecha esperada alumbramiento -->
                          <template v-if="pregunta.code === 23 && persona.sex === 'F'">
                            <TextComponent
                              :form="persona"                     
                              name="fechaPregunta22"
                              type="date"
                              class="d-inline-block"
                              rules="date_min:today|date_max:today+9m"                  
                            />
                          </template>
                          <!-- P24 mujeres: 4 campos numéricos -->
                          <template v-else-if="pregunta.code === 24 && persona.sex === 'F'">
                            <b-row no-gutters class="cuestionario-p24-filas">
                              <b-col cols="6" md="3" class="pr-1 mb-1">
                                <TextComponent
                                  :form="persona.detallePregunta24"
                                  name="nEmbarazos"
                                  label="N° Embarazos"
                                  type="number"
                                  step="1"
                                  rules="required|min_value:0|max_value:20"
                                />
                              </b-col>
                              <b-col cols="6" md="3" class="pr-1 mb-1">
                                <TextComponent
                                  :form="persona.detallePregunta24"
                                  name="nPartos"
                                  label="N° Partos"
                                  type="number"
                                  step="1"
                                  :rules="`required|min_value:0|max_value:${persona.detallePregunta24.nEmbarazos || 0}`"
                                />
                              </b-col>
                              <b-col cols="6" md="3" class="pr-1 mb-1">
                                <TextComponent
                                  :form="persona.detallePregunta24"
                                  name="nAbortos"
                                  label="N° Abortos"
                                  type="number"
                                  step="1"
                                  rules="required|min_value:0|max_value:20"
                                />
                              </b-col>
                              <b-col cols="6" md="3" class="pr-1 mb-1">
                                <TextComponent
                                  :form="persona.detallePregunta24"
                                  name="nCesareas"
                                  label="N° Cesáreas"
                                  type="number"
                                  step="1"
                                  :rules="`required|min_value:0|max_value:${persona.detallePregunta24.nPartos || 0}`"
                                />
                              </b-col>
                            </b-row>
                          </template>
                          <b-form-radio-group
                            v-else
                            v-model="persona.respuestas[indexPregunta]"
                            :options="control.TIPOS_SINO"
                            buttons
                            button-variant="outline-primary"
                            size="sm"
                            class="cuestionario-sino"
                            :name="'p-' + indexPersona + '-' + indexPregunta"
                          />
                        </b-col>
                      </b-row>
                    </b-form-group>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>

            <b-alert v-if="personasConDatos.length === 0" show variant="info">
              No hay personas con datos cargados. Complete el paso anterior (Relación de personas a asegurar).
            </b-alert>

            <b-row v-else align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import TextComponent from "@/components/controls/TextComponent.vue";
import { PREGUNTAS_CUESTIONARIO_MEDICO, TIPOS_SINO } from "@/data/colombia/salud/Selects";

const CANTIDAD_PREGUNTAS = PREGUNTAS_CUESTIONARIO_MEDICO.length;
const MAX_BENEFICIARIOS_SALUD = 6;

export default {
  name: "FormColombiaSalud03",
  components: {  TextComponent },
  data() {
    return {
      control: { TIPOS_SINO },
      PREGUNTAS_CUESTIONARIO_MEDICO,
      personasAbiertas: [],
    };
  },
  computed: {
    form() {
      return this.$store.state.wizard.form || {};
    },
    /** Cada entrada existe porque se agregó en Form02 y debe estar completa. */
    personasConDatos() {
      const list = this.form.dependientes;
      if (!list || !Array.isArray(list)) return [];
      return list;
    },
  },
  created() {
    const form = this.$store.state.wizard.form;
    if (!form || form === null) return;
    if (
      (!form.dependientes || !Array.isArray(form.dependientes) || form.dependientes.length === 0) &&
      form.beneficiarios &&
      Array.isArray(form.beneficiarios) &&
      form.beneficiarios.length > 0
    ) {
      form.dependientes = JSON.parse(JSON.stringify(form.beneficiarios));
    }
    if (!form.dependientes || !Array.isArray(form.dependientes)) {
      form.dependientes = [];
    }
    if (form.dependientes.length > MAX_BENEFICIARIOS_SALUD) {
      form.dependientes = form.dependientes.slice(0, MAX_BENEFICIARIOS_SALUD);
    }
    form.dependientes.forEach((persona) => {
      if (!persona.respuestas || persona.respuestas.length !== CANTIDAD_PREGUNTAS) {
        this.$set(persona, "respuestas", Array(CANTIDAD_PREGUNTAS).fill(""));
      }
      if (persona.sex === "F") {
        if (persona.fechaPregunta22 === undefined) this.$set(persona, "fechaPregunta22", "");
        if (!persona.detallePregunta24 || typeof persona.detallePregunta24 !== "object") {
          this.$set(persona, "detallePregunta24", {
            nEmbarazos: "",
            nPartos: "",
            nAbortos: "",
            nCesareas: "",
          });
        } else {
          const d = persona.detallePregunta24;
          if (d.nEmbarazos === undefined) this.$set(d, "nEmbarazos", "");
          if (d.nPartos === undefined) this.$set(d, "nPartos", "");
          if (d.nAbortos === undefined) this.$set(d, "nAbortos", "");
          if (d.nCesareas === undefined) this.$set(d, "nCesareas", "");
        }
      }
    });
    this.personasAbiertas = form.dependientes.map((_, i) => i === 0);
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const form = this.$store.state.wizard.form;
    if (form === undefined || form === null) {
      this.$router.push("/form/colombia/salud/");
      this.$store.dispatch("app/loading", false);
      return;
    }
    (form.dependientes || []).forEach((persona) => {
      if (persona.sex === "F") {
        if (persona.fechaPregunta22 === undefined) this.$set(persona, "fechaPregunta22", "");
        if (!persona.detallePregunta24 || typeof persona.detallePregunta24 !== "object") {
          this.$set(persona, "detallePregunta24", {
            nEmbarazos: "",
            nPartos: "",
            nAbortos: "",
            nCesareas: "",
          });
        }
      }
    });
    this.$store.dispatch("app/loading", false);
  },
  methods: {
    preguntasCount(persona) {
      return persona.sex === "F" ? 22 : 21;
    },
    contestadasCount(persona) {
      const r = persona.respuestas || [];
      if (persona.sex === "F") {
        let count = 0;
        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].forEach((i) => {
          if (i < r.length && (r[i] === "S" || r[i] === "N")) count++;
        });
        if (persona.fechaPregunta22 && String(persona.fechaPregunta22).trim()) count++;
        const d = persona.detallePregunta24;
        if (d && (d.nEmbarazos !== "" || d.nPartos !== "" || d.nAbortos !== "" || d.nCesareas !== "")) count += 1;
        return count;
      }
      const max = 21;
      let count = 0;
      for (let i = 0; i < max && i < r.length; i++) {
        if (r[i] === "S" || r[i] === "N") count++;
      }
      return count;
    },
    togglePersona(index) {
      this.$set(this.personasAbiertas, index, !this.personasAbiertas[index]);
    },
    marcarTodoNo(persona) {
      const r = persona.respuestas || [];
      const indices =
        persona.sex === "F"
          ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21]
          : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
      indices.forEach((i) => this.$set(r, i, "N"));
    },
    limpiarSeccionesPersona(persona) {
      const n = PREGUNTAS_CUESTIONARIO_MEDICO.length;
      this.$set(persona, "respuestas", Array(n).fill(""));
      if (persona.sex === "F") {
        this.$set(persona, "fechaPregunta22", "");
        this.$set(persona, "detallePregunta24", {
          nEmbarazos: "",
          nPartos: "",
          nAbortos: "",
          nCesareas: "",
        });
      }
    },
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 2, form: this.form })
        .then(() => this.$router.push("/form/colombia-salud-2.html"));
    },
    next() {
      if (!this.personasConDatos.length) {
        this.$bvToast.toast("No hay personas cargadas para el cuestionario. Vuelva al paso anterior.", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }
      const incompletos = this.personasConDatos.some((p) => {
        const r = p.respuestas || [];
        if (p.sex === "F") {
          const indicesSino = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
          if (r.length < 22) return true;
          return indicesSino.some((i) => r[i] !== "S" && r[i] !== "N");
        }
        const max = 21;
        if (r.length < max) return true;
        for (let i = 0; i < max; i++) {
          if (r[i] !== "S" && r[i] !== "N") return true;
        }
        return false;
      });
      if (incompletos) {
        this.$bvToast.toast("Debe responder Sí o No a todas las preguntas de cada persona.", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }
      const todoNo = this.personasConDatos.every((p) => {
        const r = p.respuestas || [];
        if (p.sex === "F") {
          const indicesSino = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
          return indicesSino.every((i) => r[i] === "N");
        }
        for (let i = 0; i < 21; i++) {
          if (r[i] !== "N") return false;
        }
        return true;
      });
      this.form.saltoForm04 = !!todoNo;
      if (todoNo) {
        this.form.padecimientos = [];
      }
      this.$store.dispatch("wizard/save", { index: 3, form: this.form }).then(() => {
        if (todoNo) {
            if (this.form.observations.includes('CO203')) {
              this.$router.push("/form/colombia-salud-6.html");
            } else {
              this.$router.push("/form/colombia-salud-5.html");
            }
        } else {
          this.$router.push("/form/colombia-salud-4.html");
        }
      });
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
}
.cuestionario-pregunta {
  margin-bottom: 0.5rem;
}
.cuestionario-row {
  min-height: 2.25rem;
}
/* Colores primario y secundario del proyecto */
.cuestionario-persona-header {
  cursor: pointer;
  background-color: rgba(0, 39, 118, 0.08) !important;
  border-left: 3px solid var(--one);
}
.cuestionario-persona-header:hover {
  background-color: rgba(0, 39, 118, 0.12) !important;
}
.cuestionario-persona-nombre {
  color: var(--one) !important;
}
.cuestionario-persona-chevron {
  color: var(--two) !important;
}
.cuestionario-contadas {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--four);
}
.cuestionario-sino >>> .btn-outline-primary {
  color: var(--secondary) !important;
  border-color: var(--secondary) !important;
}

/* Cambiar color de hover a azul */
.cuestionario-sino >>> .btn-outline-primary:hover {
  background-color: var(--secondary) !important;
  border-color: var(--secondary) !important;
  color: #fff !important;
}

/* Cambiar color de hover a dorado */
.cuestionario-sino >>> .btn-outline-primary:first-child:hover {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  color: #fff !important;
}

/* Cambiar color de active a azul */
.cuestionario-sino >>> .btn-outline-primary.active,
.cuestionario-sino >>> .btn-check:checked + .btn-outline-primary {
  background-color: var(--secondary) !important;
  border-color: var(--secondary) !important;
  color: #fff !important;
}

/* Cambiar color de active a dorado */
.cuestionario-sino >>> .btn-outline-primary:first-child.active,
.cuestionario-sino >>> .btn-outline-primary:first-child.btn-active {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  color: #fff !important;
}

@media only screen and (max-width: 1000px) {
  .cuestionario-persona-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .cuestionario-persona-nombre {
    flex: 1 1 100%;
    min-width: 0;
    word-break: break-word;
  }
  .cuestionario-persona-header > span.d-flex.align-items-center {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .cuestionario-row {
    flex-direction: column;
    align-items: stretch;
    min-height: 0;
  }
  .cuestionario-row .col,
  .cuestionario-row [class*="col-"] {
    max-width: 100%;
    flex: 0 0 100%;
  }
  .cuestionario-row .pr-2 {
    padding-right: 0 !important;
    margin-bottom: 0.25rem;
  }
  .cuestionario-sino-wrap {
    margin-top: 0.25rem;
  }
  .cuestionario-p24-filas [class*="col-"] {
    flex: 0 0 100%;
    max-width: 100%;
  }
  .cuestionario-p24-filas .mb-1 {
    margin-bottom: 0.5rem !important;
  }
}
</style>
